import { FunctionComponent } from 'react';
import { Close, Tick } from '@/icons';
import { Button, Text, Textinput } from '@/atoms';
import { ButtonVariant } from '@/atoms/button/types';
import { Form, Formik } from 'formik';
import toast, { Toaster } from 'react-hot-toast';
import { debug } from 'util';
import { object, ref, string } from 'yup';
import { TypeResetPasswordFormProps } from './types';

export const ResetPasswordForm: FunctionComponent<
  TypeResetPasswordFormProps
> = ({
  className = '',
  title,
  description,
  passwordLabel,
  repeatPasswordLabel,
  textButton,
  backgroundColor,
}: TypeResetPasswordFormProps) => {
  const RegisterSchema = object().shape({
    password: string()
      .min(8, 'La contraseña es muy corta')
      .max(16, 'La contraseña es muy larga')
      .required('La contraseña es obligatoria'),
    repeatPassword: string()
      .min(8, 'La contraseña es muy corta')
      .max(16, 'La contraseña es muy larga')
      .required('La contraseña es obligatoria')
      .oneOf([ref('password'), null], 'La contraseña no coincide'),
  });

  const initialValues: TypeResetPasswordFormProps = {
    backgroundColor: '',
    title: '',
    passwordLabel: '',
    repeatPasswordLabel: '',
    textButton: '',
  };

  const error = () =>
    toast('Ha habido un error, vuelve a intentarlo por favor.', {
      icon: (
        <Close
          width={64}
          color="#fff"
          className="p-3 rounded-full bg-tertiary-500 font-bold"
        />
      ),
      duration: 2000,
      style: {
        background: '#ffa59c',
        padding: '16px',
        fontWeight: 'bold',
        marginBottom: '80px',
        color: '#fff',
      },
    });

  const success = () =>
    toast('La contraseña se ha cambiado correctamente.', {
      icon: (
        <Tick
          width={64}
          color="#fff"
          className="p-3 rounded-full bg-status-success font-bold"
        />
      ),
      duration: 2000,
      style: {
        background: '#575757',
        padding: '16px',
        fontWeight: 'bold',
        marginBottom: '80px',
        color: '#fff',
      },
    });

  return (
    <div
      style={{ backgroundColor: backgroundColor }}
      className={`flex flex-col ${className}`}
    >
      <Text className="text-subtitle text-subtitle--l mb-6">{title}</Text>
      {description && (
        <Text className="text-body text-body--m mb-6">{description}</Text>
      )}

      <Formik
        onSubmit={() => {
          debug('submitted');
        }}
        validationSchema={RegisterSchema}
        initialValues={initialValues}
        validateOnMount
      >
        {({ isValidating, isValid }) => (
          <Form>
            <div className="flex flex-wrap justify-between">
              <Textinput
                label={passwordLabel}
                name="password"
                type="password"
                className="mb-2 w-full md:min-w-[300px]"
                placeholder="Introduce la nueva contraseña"
              />
              <Textinput
                label={repeatPasswordLabel}
                name="repeatPassword"
                type="password"
                className="mb-2 w-full md:min-w-[300px]"
                placeholder="Repite la contraseña"
              />

              <div>
                <Button
                  type="submit"
                  variant={ButtonVariant.PRIMARY}
                  disabled={!isValidating && !isValid ? true : false}
                  onClick={() => {
                    isValid === true || isValidating === true
                      ? success()
                      : error();
                  }}
                >
                  {textButton}
                </Button>
              </div>

              <Toaster
                position="bottom-center"
                containerStyle={{ position: 'absolute' }}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

ResetPasswordForm.displayName = 'ResetPasswordForm';
